import type { KkmServerCommand } from './protocol';

/**
 * Класс таймаута задаётся тем, что физически делает устройство:
 * - `health` — опрос состояния, ничего не печатается и деньги не двигаются;
 * - `kkt` — печать кассового чека;
 * - `shift` — открытие/закрытие смены и X-отчёт: печать отчёта по всем счётчикам
 *   смены — самая долгая операция ККТ;
 * - `acquiring` — операции с картой, включающие диалог с держателем карты.
 */
export type KkmServerTimeoutClass = 'acquiring' | 'health' | 'kkt' | 'shift';

export interface KkmServerTimeoutBudget {
  /** Поле `Timeout` команды: сколько ждёт сам KKMServer. */
  commandTimeoutSeconds: number;
  /** Browser watchdog: сколько ждёт вкладка. Всегда строго больше серверного timeout. */
  watchdogMs: number;
}

export interface KkmServerProtocolProfile {
  bridgeInjectionWatchdogMs: number;
  /**
   * Коды, которыми терминал отвечает на `CancelPaymentByPaymentCard`, когда по карте
   * отменять нечего: списания не было.
   */
  cancelNothingChargedCodes: readonly string[];
  cancelActiveCommand: ((activeCommand: KkmServerCommand, cancellationId: string) => KkmServerCommand) | null;
  /** Коды, которыми терминал сообщает об отмене операции человеком у самого терминала. */
  paymentCancelCodes: readonly string[];
  paymentDeclineCodes: readonly string[];
  pollIntervalMs: number;
  timeouts: Record<KkmServerTimeoutClass, KkmServerTimeoutBudget>;
  version: string;
}

/** Очередь KKMServer из профиля площадки от 27.07.2026: команда может простоять в ней целиком. */
const QUEUE_WAIT_SECONDS = 30;

/** Сериализация ответа и доставка callback через browser bridge. */
const TRANSPORT_RESERVE_SECONDS = 5;

/**
 * watchdog = серверный timeout + очередь + транспорт.
 *
 * Инвариант: watchdog строго больше серверного timeout. Иначе вкладка объявит исход
 * неизвестным раньше, чем KKMServer успеет вернуть достоверный ответ — ровно тот
 * сценарий, из которого вырастает повторная печать уже напечатанного чека.
 */
function browserWatchdogMs(commandTimeoutSeconds: number): number {
  return (commandTimeoutSeconds + QUEUE_WAIT_SECONDS + TRANSPORT_RESERVE_SECONDS) * 1_000;
}

/**
 * Профиль установленной версии KKMServer.
 *
 * Polling `GetRezult` — 2 секунды из официального sample. Команда отмены активного
 * PIN-dialog не включена, пока её exact contract не подтверждён на GateSbRf.
 */
export const KKM_SERVER_2_3_16_36_PROFILE: KkmServerProtocolProfile = {
  bridgeInjectionWatchdogMs: 5_000,
  // Список пуст до проверки на GateSbRf: без подтверждённого кода ответ «отменять нечего»
  // не отличить от сбоя, и экран отмены остаётся открытым.
  cancelNothingChargedCodes: [],
  cancelActiveCommand: null,
  // Собрано на стенде 22.08.2026: `2000` приходит при нажатии «Отмена» на терминале,
  // `4521` — при отказе банка или хоста. Оба ответа содержат `Status 2`, `Amount 0`
  // и пустые RRN/AC, то есть операция заведомо не выполнена.
  // `4462` — код ответа хоста 62 («карта ограничена», семейство `44ХХ` протокола pilot_nt),
  // подтверждён на проде 02.09.2026: `Status 2`, пустые RRN/AC, списания нет.
  // `4451` — недостаточно средств, подтверждён на проде 13.09.2026 (KKMServer 2.3.16.40):
  // `Status 2`, `Amount 0`, пустые RRN/AC/UniversalID; GetRezult повторяет отказ.
  // `4455` — неверный PIN, подтверждён на проде 21.09.2026 (KKMServer 2.3.16.37):
  // `Status 2`, `Amount 0`, пустые RRN/AC/UniversalID; GetRezult повторяет отказ.
  // `4478` — отказ хоста, подтверждён на проде 11–13.10.2026: `Status 2`, `Amount 0`,
  // пустые RRN/AC/UniversalID; GetRezult повторил ответ.
  // Остальные `44ХХ` в список не обобщаются: среди кодов хоста есть «ответ получен слишком поздно»
  // (4468) и сбои хоста, при которых банк мог провести операцию, — такие ответы остаются
  // `unknown`: администратор отменяет такой платёж на терминале.
  paymentCancelCodes: ['2000'],
  paymentDeclineCodes: ['2002', '4451', '4455', '4462', '4478', '4521'],
  pollIntervalMs: 2_000,
  timeouts: {
    // `Timeout: 120` и callback watchdog `305 * 1000` — из установленного sample
    // (`Samples.html`, строки 1110–1124 и 1143–1159). Формула дала бы 155 с, но
    // подтверждённое вендором значение больше, и оно же покрывает диалог с
    // держателем карты (ввод PIN, выбор счёта), поэтому берём его.
    acquiring: { commandTimeoutSeconds: 120, watchdogMs: 305_000 },
    health: { commandTimeoutSeconds: 15, watchdogMs: browserWatchdogMs(15) },
    // `RegisterCheck` `Timeout: 30` — из установленного sample, строки 175–180.
    kkt: { commandTimeoutSeconds: 30, watchdogMs: browserWatchdogMs(30) },
    // Sample не документирует `Timeout` для команд смены. Печать Z-отчёта длиннее
    // печати чека — берём тройной запас относительно `kkt`. Финальное значение
    // подтверждается на стенде и уточняется здесь же.
    shift: { commandTimeoutSeconds: 90, watchdogMs: browserWatchdogMs(90) },
  },
  version: '2.3.16.36',
};

/**
 * Совместимость версии KKMServer с текущим protocol profile.
 *
 * Профиль снят с `2.3.16.36`, и вендор выкладывает только текущую сборку — старую
 * версию клубу взять неоткуда. Поэтому граница нижняя: версии не ниже профильной
 * проходят, а защиту от изменившегося формата ответа держат fail-closed парсеры и
 * тестовая печать на последнем шаге мастера. Сравнивается нормализованная базовая
 * четвёрка; версия с суффиксом (например, `2.3.16.36-dev`) допускается только при
 * `allowSuffix`, то есть вне production-сборки.
 */
export function isSupportedKkmServerVersion(version: string | null | undefined, allowSuffix: boolean): boolean {
  const normalized = normalizeKkmServerVersion(version);

  if (normalized === null) return false;

  if (compareKkmServerVersions(normalized.base, KKM_SERVER_2_3_16_36_PROFILE.version) < 0) return false;

  return normalized.suffix === null || allowSuffix;
}

/** Обе строки — базовые четвёрки из `normalizeKkmServerVersion`, сравнение по числам. */
function compareKkmServerVersions(left: string, right: string): number {
  const leftParts = left.split('.').map(Number);
  const rightParts = right.split('.').map(Number);

  for (let index = 0; index < leftParts.length; index += 1) {
    const difference = leftParts[index]! - rightParts[index]!;

    if (difference !== 0) return difference;
  }

  return 0;
}

function normalizeKkmServerVersion(version: string | null | undefined): { base: string; suffix: string | null } | null {
  const match = /^(\d+\.\d+\.\d+\.\d+)(?:-(.+))?$/.exec((version ?? '').trim());

  if (match === null) return null;

  return { base: match[1]!, suffix: match[2] ?? null };
}
